import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";

import {
  AddCircle as AddCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";

import { deleteTemporaryUser, getTemporaryUsers } from "../../../api/services/TemporaryUser/temporaryUser";
import { QK } from "../../../base/qk";
import TableComponent from "../../../components/TableComponent/TableComponent";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import TemporaryInspectorDialog from "./Dialog";

const PRIMARY_BLUE = "#004D99";
const LIGHT_BLUE_BG = "#E3F2FD";

const TemporaryInspector = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState("all"); // all / specific
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const handleOpenModal = (record = null) => {
    setSelectedRecord(record);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedRecord(null);
    setShowModal(false);
  };

  const { data, isLoading } = useQuery(
    [QK.temporaryUsers, page, filterType],
    () => getTemporaryUsers(page, filterType),
    {
      keepPreviousData: true,
      onError: () => enqueueSnackbar("Failed to load temporary users", { variant: "error" }),
    }
  );

  const deleteMutation = useMutation(
    (id) => deleteTemporaryUser(id),
    {
      onSuccess: () => {
        enqueueSnackbar("Temporary user deleted", { variant: "success" });
        queryClient.invalidateQueries(QK.temporaryUsers);
        setShowConfirm(false);
        setRecordToDelete(null);
      },
      onError: () => enqueueSnackbar("Failed to delete", { variant: "error" }),
    }
  );

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowConfirm(true);
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(recordToDelete.id);
  };

  const actions = [
    {
      icon: <EditIcon />,
      tooltip: "Edit",
      color: "primary",
      handler: (row) => handleOpenModal(row),
    },
    {
      icon: <DeleteIcon />,
      tooltip: "Delete",
      color: "error",
      handler: handleDeleteClick,
    },
  ];

  // table rows
  const tableData =
    data?.results?.map((item) => ({
      ...item,
      allowed_clients: item.allow_all_clients ? "All Clients" : `${item.clients?.length || 0} Clients`,
      active_status: item.active ? "Active" : "Inactive",
    })) || [];

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" sx={{ color: PRIMARY_BLUE, fontWeight: "bold" }}>
                Temporary Users
              </Typography>
              <Box display="flex" gap={1}>
                <Tooltip title="Add Temporary User">
                  <IconButton color="primary" onClick={() => handleOpenModal()}>
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          }
          sx={{ backgroundColor: LIGHT_BLUE_BG }}
        />

        {/* Modal */}
        <TemporaryInspectorDialog
          show={showModal}
          onHide={handleCloseModal}
          record={selectedRecord}
        />

        {/* Confirm Delete */}
        <ConfirmModal
          show={showConfirm}
          handleClose={() => setShowConfirm(false)}
          handleConfirm={handleConfirmDelete}
          title="Delete Temporary User"
          body="Are you sure? This action cannot be undone."
          isDelete
        />

        <CardContent>
          {/* Filter */}
          <RadioGroup
            row
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel value="all" control={<Radio />} label="Show All" />
            <FormControlLabel value="active" control={<Radio />} label="Active Only" />
          </RadioGroup>

          <TableComponent
            columns={[
              { title: "Name", field: "user.full_name" },
              { title: "Allowed Clients", field: "allowed_clients" },
              { title: "Start", field: "start_datetime" },
              { title: "End", field: "end_datetime" },
              { title: "Status", field: "active_status" },
            ]}
            data={tableData}
            actions={actions}
            isLoading={isLoading}
            pagination={{
              totalPages: Math.ceil((data?.count || 1) / 10),
              currentPage: page,
            }}
            onPageChange={(p) => setPage(p)}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemporaryInspector;